// „Headset prüfen“ oben rechts: Mikrofon mit Pegel und klarem Urteil, Testton auf dem gewählten Lautsprecher,
// Geräteauswahl, Leitungstest (Twilio-Preflight) nur wenn die Telefonie eingerichtet ist. Der reine Gerätetest läuft
// ganz im Browser und braucht keinen Twilio-Zugang. Nutzt von der Telefonie nur useTelefonie() (öffentliche Schnittstelle).
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { telefonieApi } from "../api";
import { useTelefonie } from "../telefonie";
import { AbsenderAuswahl, useAbsender } from "./Absendernummer";
import { Ausklapp } from "./Ausklapp";
import { telefonText } from "./Telefonnummer";
import { gesamtArt, leitungText, mikrofonFehlerText, mikrofonUrteil, pegelAnteilVon, pegelDb, type Art, type Urteil } from "./headsetLogik";
import { Icon } from "./Icon";

const ERGEBNIS_SCHLUESSEL = "crm-headset-ergebnis";

interface Gemerkt {
  art: Exclude<Art, "warte">;
  text: string;
  zeit: string;
}

function gemerktLesen(): Gemerkt | null {
  try {
    const roh = sessionStorage.getItem(ERGEBNIS_SCHLUESSEL);
    return roh ? (JSON.parse(roh) as Gemerkt) : null;
  } catch {
    return null;
  }
}

function gemerktSchreiben(g: Gemerkt | null): void {
  try {
    if (g) sessionStorage.setItem(ERGEBNIS_SCHLUESSEL, JSON.stringify(g));
    else sessionStorage.removeItem(ERGEBNIS_SCHLUESSEL);
  } catch {
    /* egal */
  }
}

/** Headset-Symbol (Bügel, zwei Muscheln, Mikrofonarm). */
export function HeadsetSymbol({ groesse = 16 }: { groesse?: number }) {
  return (
    <svg width={groesse} height={groesse} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4 14v-2a8 8 0 0116 0v2" />
      <path d="M4 14h3v5H5a1 1 0 01-1-1zM20 14h-3v5h2a1 1 0 001-1z" />
      <path d="M17 19c0 1.5-1.5 2.5-4 2.5h-1" />
    </svg>
  );
}

export function HeadsetKnopf() {
  const [offen, setOffen] = useState(false);
  const absender = useAbsender();
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const [gemerkt, setGemerkt] = useState<Gemerkt | null>(gemerktLesen);
  const tel = useTelefonie();
  // Meldet die Telefonie im Gespräch ein Mikrofonproblem, steht das auch hier (rot).
  const telWarnung = tel.warnungen?.find((w) => w.mikrofon);
  const art = telWarnung ? "rot" : gemerkt?.art ?? null;
  const titel = telWarnung
    ? `Headset prüfen – ${telWarnung.text}`
    : gemerkt
      ? `Headset prüfen – zuletzt ${gemerkt.zeit}: ${gemerkt.text}`
      : "Headset prüfen: Mikrofon, Lautsprecher und Leitung testen";
  return (
    <>
      <button
        ref={setKnopf}
        type="button"
        className={`kopf-knopf headset-knopf${offen ? " kopf-knopf--an" : ""}`}
        title={titel}
        aria-label="Headset"
        aria-haspopup="dialog"
        aria-expanded={offen}
        onClick={() => {
          setOffen((o) => !o);
        }}
      >
        <HeadsetSymbol groesse={15} />
        <span className="kopf-knopf__text">Headset</span>
        {art ? <span className={`headset-knopf__punkt headset-knopf__punkt--${art}`} aria-hidden="true" /> : null}
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} ausrichtung="end" breite={372} maxHoehe={680} rolle="dialog" className="headset">
        <div className="headset__inhalt hs-menue">
          <div className="headset__kopf">
            <HeadsetSymbol groesse={16} />
            <h2>Anrufen mit</h2>
            <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Schließen" onClick={() => setOffen(false)}>
              <Icon name="schliessen" groesse={14} />
            </button>
          </div>
          <section className="headset__teil">
            <AbsenderAuswahl />
            {absender.bereit && absender.aktuell ? <p className="hs-klein">Diese Nummer sieht der Angerufene: {telefonText(absender.aktuell)}</p> : null}
          </section>
          <HeadsetPanel
            eingebettet
            onErgebnis={(g) => {
              setGemerkt(g);
              gemerktSchreiben(g);
            }}
            onSchliessen={() => setOffen(false)}
          />
        </div>
      </Ausklapp>
    </>
  );
}

interface Messung {
  stream: MediaStream;
  ctx: AudioContext;
  /** Intervall-Nummer der Messung */
  rahmen: number;
}

type Hoeren = "offen" | "spielt" | "frage" | "ja" | "nein";

function HeadsetPanel({ onErgebnis, onSchliessen, onZurueck, eingebettet = false }: { onErgebnis: (g: Gemerkt | null) => void; onSchliessen: () => void; onZurueck?: () => void; eingebettet?: boolean }) {
  const tel = useTelefonie();
  const [eingaenge, setEingaenge] = useState<MediaDeviceInfo[]>([]);
  const [ausgaenge, setAusgaenge] = useState<MediaDeviceInfo[]>([]);
  const [eingang, setEingang] = useState("");
  const [ausgang, setAusgang] = useState("");
  const [anteil, setAnteil] = useState(0);
  const [db, setDb] = useState<number | null>(null);
  const [mikro, setMikro] = useState<Urteil>({ art: "warte", text: "Mikrofon wird geöffnet …" });
  const [hoeren, setHoeren] = useState<Hoeren>("offen");
  const [hinweis, setHinweis] = useState<string | null>(null);
  const messung = useRef<Messung | null>(null);
  const lauf = useRef(0);
  const ausgabeMoeglich = typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
  const telefonieBereit = tel.status !== "nicht_eingerichtet";

  const geraeteLaden = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const liste = await navigator.mediaDevices.enumerateDevices();
    setEingaenge(liste.filter((g) => g.kind === "audioinput"));
    setAusgaenge(liste.filter((g) => g.kind === "audiooutput"));
  }, []);

  const stoppen = useCallback(() => {
    const m = messung.current;
    if (!m) return;
    window.clearInterval(m.rahmen);
    m.stream.getTracks().forEach((t) => t.stop());
    void m.ctx.close().catch(() => undefined);
    messung.current = null;
  }, []);

  const mikrofonTesten = useCallback(
    async (geraet: string) => {
      stoppen();
      const nr = ++lauf.current;
      setAnteil(0);
      setDb(null);
      setMikro({ art: "warte", text: "Mikrofon wird geöffnet …" });
      if (!navigator.mediaDevices?.getUserMedia) {
        setMikro({ art: "rot", text: "Dieser Browser kann das Mikrofon nicht öffnen (nur über https oder die Desktop-App)." });
        return;
      }
      try {
        // Ohne Rauschunterdrückung/automatische Verstärkung: der Pegel zeigt, was das Headset wirklich liefert.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { ...(geraet ? { deviceId: { exact: geraet } } : {}), echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        if (nr !== lauf.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        void geraeteLaden(); // erst nach der Freigabe zeigt der Browser Gerätenamen
        const ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const daten = new Float32Array(analyser.fftSize);
        const spur = stream.getAudioTracks()[0];
        const beginn = performance.now();
        let spitze = -Infinity;
        let letzteAnzeige = 0;
        let letztesUrteil = "";
        const schritt = () => {
          if (nr !== lauf.current) return;
          analyser.getFloatTimeDomainData(daten);
          const jetzt = pegelDb(daten);
          if (jetzt > spitze) spitze = jetzt;
          const t = performance.now();
          if (t - letzteAnzeige > 90) {
            letzteAnzeige = t;
            setAnteil(pegelAnteilVon(jetzt));
            setDb(Number.isFinite(jetzt) ? Math.round(jetzt) : null);
            const u = mikrofonUrteil({ spitzeDb: spitze, laufzeitMs: t - beginn, stumm: spur?.muted || spur?.enabled === false });
            if (u.text !== letztesUrteil) {
              letztesUrteil = u.text;
              setMikro(u);
            }
          }
        };
        // 10 Messungen je Sekunde reichen für die Anzeige (früher 60 je Sekunde per requestAnimationFrame, Anzeige nur alle 90 ms)
        messung.current = { stream, ctx, rahmen: window.setInterval(schritt, 100) };
      } catch (e) {
        if (nr !== lauf.current) return;
        const fehler = e as { name?: string; message?: string };
        setMikro({ art: "rot", text: mikrofonFehlerText(fehler.name, fehler.message) });
      }
    },
    [stoppen, geraeteLaden],
  );

  // Beim Öffnen: gespeicherte Geräte der Telefonie vorwählen (wenn vorhanden), dann sofort messen.
  useEffect(() => {
    let weg = false;
    void (async () => {
      let ein = "";
      try {
        const e = await telefonieApi.einstellungen();
        ein = e.input_device_id ?? "";
        if (!weg) {
          setEingang(ein);
          setAusgang(e.output_device_id ?? "");
        }
      } catch {
        /* ohne Telefonie: Standardgeräte */
      }
      if (!weg && tel.aktiverAnruf) void geraeteLaden();
      if (!weg && !tel.aktiverAnruf) void mikrofonTesten(ein); // im Gespräch läuft das Mikrofon schon: Messung nur auf Knopfdruck
    })();
    const wechsel = () => void geraeteLaden();
    navigator.mediaDevices?.addEventListener?.("devicechange", wechsel);
    return () => {
      weg = true;
      lauf.current += 1;
      navigator.mediaDevices?.removeEventListener?.("devicechange", wechsel);
      stoppen();
    };
  }, [mikrofonTesten, geraeteLaden, stoppen]);

  // Gespeichertes Gerät gibt es nicht mehr (Headset abgesteckt): auf Standard zurück.
  useEffect(() => {
    if (eingang && eingaenge.length && !eingaenge.some((g) => g.deviceId === eingang)) {
      setEingang("");
      void mikrofonTesten("");
    }
  }, [eingaenge, eingang, mikrofonTesten]);

  const pf = leitungText(tel.preflight?.ergebnis ?? tel.info?.preflight ?? null);
  const hoerUrteil: Urteil | null =
    hoeren === "ja"
      ? { art: "ok", text: "Lautsprecher funktioniert." }
      : hoeren === "nein"
        ? { art: "rot", text: "Kein Ton: Lautstärke am Headset und im System prüfen, oben den richtigen Lautsprecher wählen (Bluetooth verbunden?)." }
        : null;

  // Ergebnis für den Punkt am Knopf merken, sobald es ein Urteil gibt.
  const art = gesamtArt([mikro.art, hoerUrteil?.art]);
  useEffect(() => {
    if (!art) return;
    const text = art === "ok" ? (hoerUrteil ? "Mikrofon und Lautsprecher funktionieren." : mikro.text) : [mikro.art !== "ok" ? mikro.text : null, hoerUrteil?.art === "rot" ? hoerUrteil.text : null].filter(Boolean).join(" ");
    onErgebnis({ art, text, zeit: new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art, mikro.text, hoerUrteil?.text]);

  const uebernehmen = async (ein: string, aus: string) => {
    setHinweis(null);
    if (!telefonieBereit) return;
    try {
      await tel.geraeteSetzen(ein || null, aus || null);
      setHinweis("Für Anrufe übernommen.");
    } catch (e) {
      setHinweis(`Für Anrufe nicht übernommen: ${(e as Error).message}`);
    }
  };

  const testton = async () => {
    setHoeren("spielt");
    try {
      const ctx = new AudioContext();
      const ziel = ctx.createMediaStreamDestination();
      const lautstaerke = ctx.createGain();
      lautstaerke.gain.value = 0;
      lautstaerke.connect(ziel);
      // Zwei kurze Töne (wie ein Gong), leise ein- und ausgeblendet
      const t0 = ctx.currentTime + 0.05;
      [
        [660, t0],
        [880, t0 + 0.35],
      ].forEach(([f, start]) => {
        const ton = ctx.createOscillator();
        ton.frequency.value = f;
        ton.connect(lautstaerke);
        ton.start(start);
        ton.stop(start + 0.32);
      });
      lautstaerke.gain.setValueAtTime(0, t0);
      lautstaerke.gain.linearRampToValueAtTime(0.18, t0 + 0.03);
      lautstaerke.gain.setValueAtTime(0.18, t0 + 0.62);
      lautstaerke.gain.linearRampToValueAtTime(0, t0 + 0.7);
      const audio = new Audio() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      audio.srcObject = ziel.stream;
      if (ausgang && audio.setSinkId) await audio.setSinkId(ausgang);
      await audio.play();
      window.setTimeout(() => {
        audio.pause();
        void ctx.close().catch(() => undefined);
        setHoeren("frage");
      }, 900);
    } catch (e) {
      setHoeren("nein");
      setHinweis(`Testton geht nicht: ${(e as Error).message}`);
    }
  };

  const standardName = (liste: MediaDeviceInfo[]) => {
    const std = liste.find((g) => g.deviceId === "default");
    const name = std?.label.replace(/^(Standard|Default)\s*[-–]\s*/i, "");
    return name ? `Standard (${name})` : "Standard des Systems";
  };

  return (
    <div className={eingebettet ? "headset__eingebettet" : "headset__inhalt"}>
      {eingebettet ? null : (
        <div className="headset__kopf">
        {onZurueck ? (
          <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Zurück zum Headset-Menü" title="Zurück" onClick={onZurueck}>
            <Icon name="chevron-links" groesse={15} />
          </button>
        ) : (
          <HeadsetSymbol groesse={16} />
        )}
        <h2>Audio-Einstellungen</h2>
        <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Schließen" onClick={onSchliessen}>
          <Icon name="schliessen" groesse={14} />
        </button>
      </div>
      )}

      <section className="headset__teil">
        <div className="headset__zeile">
          <span className="headset__titel">Mikrofon</span>
          <button type="button" className="textknopf" onClick={() => void mikrofonTesten(eingang)}>
            Mikrofon testen
          </button>
        </div>
        <select
          className="eingabe headset__wahl"
          aria-label="Mikrofon"
          value={eingang}
          onChange={(e) => {
            setEingang(e.target.value);
            void mikrofonTesten(e.target.value);
            void uebernehmen(e.target.value, ausgang);
          }}
        >
          <option value="">{standardName(eingaenge)}</option>
          {eingaenge
            .filter((g) => g.deviceId && g.deviceId !== "default" && g.deviceId !== "communications")
            .map((g) => (
              <option key={g.deviceId} value={g.deviceId}>
                {g.label || "Mikrofon"}
              </option>
            ))}
        </select>
        <div className={`pegel pegel--${mikro.art}`} role="meter" aria-label="Mikrofonpegel" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(anteil * 100)}>
          <div className="pegel__fuellung" style={{ width: `${Math.round(anteil * 100)}%` }} />
        </div>
        <p className={`headset__urteil headset__urteil--${mikro.art}`} role="status">
          <UrteilSymbol art={mikro.art} />
          <span>
            {mikro.text}
            {db !== null && mikro.art !== "rot" ? <span className="leise"> ({db} dBFS)</span> : null}
          </span>
        </p>
      </section>

      <section className="headset__teil">
        <span className="headset__titel">Lautsprecher</span>
        {ausgabeMoeglich && ausgaenge.length ? (
          <select
            className="eingabe headset__wahl"
            aria-label="Lautsprecher"
            value={ausgang}
            onChange={(e) => {
              setAusgang(e.target.value);
              setHoeren("offen");
              void uebernehmen(eingang, e.target.value);
            }}
          >
            <option value="">{standardName(ausgaenge)}</option>
            {ausgaenge
              .filter((g) => g.deviceId && g.deviceId !== "default" && g.deviceId !== "communications")
              .map((g) => (
                <option key={g.deviceId} value={g.deviceId}>
                  {g.label || "Lautsprecher"}
                </option>
              ))}
          </select>
        ) : !ausgabeMoeglich ? (
          <p className="headset__klein">Dieser Browser spielt immer über den Standard-Lautsprecher des Systems.</p>
        ) : null}
        <div className="headset__zeile">
          <button type="button" className="knopf knopf--klein" disabled={hoeren === "spielt"} onClick={() => void testton()}>
            <Icon name="pfeil-rechts" groesse={13} />
            {hoeren === "spielt" ? "Spielt …" : "Testton"}
          </button>
          {hoeren === "frage" ? (
            <span className="headset__frage">
              Gehört?
              <button type="button" className="knopf knopf--klein" onClick={() => setHoeren("ja")}>
                Ja
              </button>
              <button type="button" className="knopf knopf--klein" onClick={() => setHoeren("nein")}>
                Nein
              </button>
            </span>
          ) : null}
        </div>
        {hoerUrteil ? (
          <p className={`headset__urteil headset__urteil--${hoerUrteil.art}`} role="status">
            <UrteilSymbol art={hoerUrteil.art} />
            <span>{hoerUrteil.text}</span>
          </p>
        ) : null}
      </section>

      <section className="headset__teil">
        <span className="headset__titel">Leitung</span>
        {telefonieBereit ? (
          <>
            <div className="headset__zeile">
              <button type="button" className="knopf knopf--klein" disabled={tel.preflight?.laeuft || !!tel.aktiverAnruf} onClick={() => void tel.preflightStarten()}>
                {tel.preflight?.laeuft ? "Test läuft (etwa 10 s) …" : "Leitung testen"}
              </button>
              {tel.aktiverAnruf ? <span className="headset__klein">nach dem Gespräch</span> : null}
            </div>
            {tel.preflight?.fehler && !pf ? (
              <p className="headset__urteil headset__urteil--rot">
                <UrteilSymbol art="rot" />
                <span>{tel.preflight.fehler}</span>
              </p>
            ) : pf ? (
              <p className={`headset__urteil headset__urteil--${pf.art}`}>
                <UrteilSymbol art={pf.art} />
                <span>{pf.text}</span>
              </p>
            ) : (
              <p className="headset__klein">Heute noch nicht getestet. Der Test ruft kurz bei Twilio an und misst Verzögerung und Qualität.</p>
            )}
          </>
        ) : (
          <p className="headset__klein">
            {tel.grund ?? "Die Telefonie ist noch nicht eingerichtet."} Den Leitungstest gibt es danach, Mikrofon und Lautsprecher lassen sich schon jetzt prüfen.{" "}
            <Link to="/einstellungen#telefonie" onClick={onSchliessen}>
              Telefonie-Einstellungen
            </Link>
          </p>
        )}
      </section>
      {hinweis ? <p className="headset__klein headset__hinweis">{hinweis}</p> : null}
    </div>
  );
}

function UrteilSymbol({ art }: { art: Art }) {
  if (art === "ok") return <Icon name="erledigt" groesse={15} />;
  if (art === "warte") return <span className="headset__warte" aria-hidden="true" />;
  return <Icon name="warnung" groesse={15} />;
}
